import {
  getPayRange,
  NumberUtils,
  PaymentMethod,
  PaymentProvider,
  RegionCode,
  ValidationUtils,
} from '@ca/common'
import type { OrderScenario } from '@ca/common/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { getInvoiceFee } from '@order/hooks/useInvoiceFee'
import { usePaymentValues } from '@order/hooks/usePaymentValues'
import { getMethodSettings } from '@order/hooks/usePaymentMethods'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useStore } from 'hooks/useSession'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { useCurrencyDecimals } from 'hooks/useCurrency'

const SEPA_IBAN_COUNTRIES: Partial<Record<RegionCode, string[]>> = {
  de: ['DE'],
  at: ['AT'],
}

export type PaymentFormValues = {
  amount: string
  method: PaymentMethod
  reader_id: number | string | null
  iban: string
  holder: string
  privacy: boolean
  sepa: boolean
  terms: boolean
}

type PayRange = [number, number]

// Stored metadata applies to every method; fetched metadata only where staff
// picks the amount, since fixed amounts may carry client-side fees
const resolveRange = ({
  storedRange,
  fetchedRange,
  method,
  provider,
  scenarios,
}: {
  storedRange: PayRange | null
  fetchedRange: PayRange | null
  method: PaymentMethod
  provider?: PaymentProvider
  scenarios?: OrderScenario[]
}) => {
  if (storedRange) {
    return storedRange
  }
  const settings = getMethodSettings(method, provider, scenarios)
  return settings?.custom === 'enabled' ? fetchedRange : null
}

const usePaymentForm = ({
  defaultMethod,
  getPaymentValues,
  defaultReaderId,
  metadataPayRange,
  fetchedPayRange,
  provider,
  scenarios,
}: {
  defaultReaderId?: number | string | null
  defaultMethod: PaymentMethod
  getPaymentValues: ReturnType<typeof usePaymentValues>
  metadataPayRange?: number[] | null
  fetchedPayRange?: number[] | null
  provider?: PaymentProvider
  scenarios?: OrderScenario[]
}) => {
  const store = useStore()
  const config = useRemoteConfig()
  const decimals = useCurrencyDecimals()

  const region = config?.region.countryCode
  const schema = useMemo(() => {
    const storedRange = getPayRange(metadataPayRange)
    const fetchedRange = getPayRange(fetchedPayRange)

    const allowedIbanCountries = region
      ? SEPA_IBAN_COUNTRIES[region]
      : undefined

    return z
      .object({
        amount: z
          .string()
          .transform(NumberUtils.parse.toFloatOrNull)
          .refine((val) => val !== null, { message: 'amount.invalid' })
          .refine((val) => Number.isFinite(val), { message: 'amount.invalid' }),
        method: z.nativeEnum(PaymentMethod),
        reader_id: z.union([z.number(), z.string(), z.null()]),
        iban: z
          .string()
          .refine(
            (v) => !v || ValidationUtils.iban.validate(v, allowedIbanCountries),
            'iban.invalid'
          )
          .or(z.literal('')),
        holder: z.string(),
        privacy: z.boolean(),
        sepa: z.boolean(),
        terms: z.boolean(),
      })
      .superRefine((data, ctx) => {
        const settings = getMethodSettings(data.method)
        const { toPay } = getPaymentValues(data.method)
        const fee = getInvoiceFee(store, data.method)

        const range = resolveRange({
          storedRange,
          fetchedRange,
          method: data.method,
          provider,
          scenarios,
        })

        const computedMin = NumberUtils.ceil.decimal(toPay / 2, decimals)
        const computedMax = toPay + (fee ?? 0)
        const min = range ? range[0] : computedMin
        const max = range ? range[1] : computedMax
        if (data.amount == null) {
          ctx.addIssue({
            code: 'custom',
            message: 'required',
            path: ['amount'],
          })
        }
        if (data.amount && data.amount < min) {
          ctx.addIssue({
            code: 'custom',
            message: 'amount.min',
            path: ['amount'],
          })
        }

        if (data.amount && data.amount > max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'amount.max',
            path: ['amount'],
          })
        }

        if (settings.privacy && !data.privacy) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['privacy'],
          })
        }
        if (settings.terms && !data.terms) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['terms'],
          })
        }
        if (settings.sepa && !data.sepa) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['sepa'],
          })
        }

        if (settings.iban) {
          if (!data.iban) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'required',
              path: ['iban'],
            })
          }
          if (!data.holder) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'required',
              path: ['holder'],
            })
          }
        }
      })
  }, [
    region,
    getPaymentValues,
    store,
    metadataPayRange,
    fetchedPayRange,
    provider,
    scenarios,
    decimals,
  ])

  const defaults = useMemo(
    () => getPaymentValues(defaultMethod),
    [getPaymentValues, defaultMethod]
  )

  const min = NumberUtils.ceil.decimal(defaults.toPay / 2, decimals)
  const form = useForm<PaymentFormValues, any, PaymentFormValues>({
    defaultValues: {
      amount: `${min ?? ''}`,
      method: defaultMethod,
      reader_id: defaultReaderId ?? null,
      iban: '',
      holder: '',
      privacy: false,
      sepa: false,
      terms: false,
    },
    // react-hook-form ≥7.55: zodResolver infers z.input<schema> as TFieldValues; the inline schema
    // transforms amount string→number|null, making input≠output types. `as any` preserves
    // pre-7.55 behavior — Zod still validates and transforms at runtime.
    // See docs/expo54-migration.md § Known Issues.
    resolver: zodResolver(schema) as any,
  })

  useEffect(() => {
    if (defaultReaderId && form.getValues('reader_id') === null) {
      form.setValue('reader_id', defaultReaderId)
    }
  }, [defaultReaderId, form])

  const method = useWatch({ name: 'method', control: form.control })

  const max = useMemo(() => {
    const range = resolveRange({
      storedRange: getPayRange(metadataPayRange),
      fetchedRange: getPayRange(fetchedPayRange),
      method,
      provider,
      scenarios,
    })
    if (range) {
      return range[1]
    }
    const { toPay } = getPaymentValues(method)
    const fee = getInvoiceFee(store, method)
    return toPay + (fee ?? 0)
  }, [
    method,
    getPaymentValues,
    store,
    metadataPayRange,
    fetchedPayRange,
    provider,
    scenarios,
  ])

  useEffect(() => {
    const currentAmount = NumberUtils.parse.toFloatOrNull(
      form.getValues('amount')
    )
    if (currentAmount == null) {
      return
    }
    if (currentAmount > max) {
      form.setValue('amount', `${max}`, { shouldValidate: false })
    }
  }, [max, form])

  return form
}

export type PaymentForm = ReturnType<typeof usePaymentForm>
export type PaymentFormControl = PaymentForm['control']

export default usePaymentForm
